import React from 'react';
import { SERVICES } from './landingData';

/* ─────────────────────────────────────────────
   STYLES
───────────────────────────────────────────── */
const styles = `
  /* Variables and font imports are handled by global.css */

  /* ── Section ── */
  .contact-section {
    padding: 100px 60px;
    border-top: 1px solid var(--border-soft);
    background: var(--card-bg);
    position: relative;
    overflow: hidden;
  }
  .contact-section::before {
    content: '';
    position: absolute; inset: 0; pointer-events: none;
    background: radial-gradient(ellipse 55% 50% at 15% 50%, rgba(245,200,0,0.04) 0%, transparent 65%);
  }

  .contact-inner {
    display: grid;
    grid-template-columns: 1fr 1.35fr;
    gap: 72px;
    align-items: flex-start;
    position: relative; z-index: 1;
  }

  /* ── LEFT PANEL ── */
  .contact-eyebrow {
    font-size: 10px; letter-spacing: 4px; text-transform: uppercase;
    color: var(--accent); margin-bottom: 14px;
    display: flex; align-items: center; gap: 10px;
  }
  .contact-eyebrow::before {
    content: ''; width: 24px; height: 1px;
    background: var(--accent); display: inline-block;
  }
  .contact-heading {
    font-family: 'Bebas Neue', sans-serif;
    font-size: clamp(40px, 5vw, 68px);
    line-height: .95; color: var(--white); margin-bottom: 20px;
  }
  .contact-heading em {
    font-family: 'Instrument Serif', serif; font-style: italic; color: var(--gold);
  }
  .contact-desc {
    font-size: 14px; line-height: 1.85; color: var(--muted-light); margin-bottom: 36px;
  }

  /* Info items */
  .contact-info { display: flex; flex-direction: column; gap: 0; }
  .ci-item {
    display: flex; align-items: center; gap: 14px;
    padding: 14px 0; border-bottom: 1px solid var(--border-soft);
    transition: border-color .25s;
  }
  .ci-item:last-child { border-bottom: none; }
  .ci-item:hover { border-color: rgba(245,200,0,0.15); }
  .ci-icon {
    width: 36px; height: 36px; border-radius: 6px;
    background: rgba(245,200,0,0.08);
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0; transition: background .25s;
  }
  .ci-item:hover .ci-icon { background: rgba(245,200,0,0.14); }
  .ci-icon svg {
    width: 14px; height: 14px; stroke: var(--accent); fill: none;
    stroke-width: 1.5; stroke-linecap: round; stroke-linejoin: round;
  }
  .ci-label {
    font-size: 9px; letter-spacing: 2px; text-transform: uppercase;
    color: var(--muted); margin-bottom: 2px;
  }
  .ci-text { font-size: 13px; color: var(--muted-light); line-height: 1.5; }

  /* ── WhatsApp CTA ── */
  .wa-btn {
    display: flex; align-items: center; gap: 16px;
    background: rgba(37,211,102,0.08);
    border: 1px solid rgba(37,211,102,0.25);
    border-radius: 10px; padding: 16px 20px;
    cursor: pointer; text-decoration: none;
    color: inherit;
    transition: background .25s, border-color .25s, transform .2s;
    width: 100%; box-sizing: border-box;
  }
  .wa-btn:hover {
    background: rgba(37,211,102,0.14);
    border-color: rgba(37,211,102,0.45);
    transform: translateY(-2px);
  }
  .wa-icon-wrap {
    width: 48px; height: 48px; border-radius: 12px;
    background: #25d366;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
  }
  .wa-icon-wrap svg { width: 26px; height: 26px; fill: #fff; }
  .wa-text { flex: 1; }
  .wa-title {
    font-size: 14px; font-weight: 500; color: #25d366;
    margin-bottom: 3px; letter-spacing: .3px;
  }
  .wa-sub { font-size: 12px; color: var(--muted-light); line-height: 1.5; }
  .wa-arrow {
    font-size: 18px; color: var(--muted-light);
    transition: color .2s, transform .2s;
  }
  .wa-btn:hover .wa-arrow { color: #25d366; transform: translateX(3px); }

  /* Divider */
  .wa-divider {
    display: flex; align-items: center; gap: 14px;
    margin: 4px 0;
  }
  .wa-divider::before, .wa-divider::after {
    content: ''; flex: 1; height: 1px; background: var(--border-soft);
  }
  .wa-divider span {
    font-size: 9px; letter-spacing: 3px; text-transform: uppercase;
    color: var(--muted); white-space: nowrap;
  }

  /* ── RIGHT: FORM ── */
  .contact-form { display: flex; flex-direction: column; gap: 14px; }
  .form-row-2   { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
  .form-field   { display: flex; flex-direction: column; gap: 6px; }

  .field-label {
    font-size: 9px; letter-spacing: 2px; text-transform: uppercase;
    color: var(--muted-light);
  }
  .field-required { color: var(--accent); margin-left: 2px; }

  /* Input wrapper with icon */
  .input-wrap { position: relative; display: flex; align-items: center; }
  .input-icon {
    position: absolute; left: 14px;
    width: 14px; height: 14px; stroke: var(--muted); fill: none;
    stroke-width: 1.5; stroke-linecap: round; stroke-linejoin: round;
    pointer-events: none; flex-shrink: 0;
  }

  .f-input, .f-select, .f-textarea {
    background: rgba(245,200,0,0.04);
    border: 1px solid rgba(245,200,0,0.14);
    color: var(--white); font-size: 13px;
    font-family: 'DM Sans', sans-serif; font-weight: 300;
    border-radius: 4px; outline: none; width: 100%;
    transition: border-color .25s, background .25s;
  }
  .f-input, .f-select { padding: 13px 16px; }
  .f-input.has-icon    { padding-left: 40px; }
  .f-input:hover,  .f-select:hover,  .f-textarea:hover  { border-color: rgba(245,200,0,0.25); }
  .f-input:focus,  .f-select:focus,  .f-textarea:focus  { border-color: var(--accent); background: rgba(245,200,0,0.06); }
  .f-input::placeholder, .f-textarea::placeholder { color: var(--muted); }
  .f-select {
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%238a8060' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
    background-repeat: no-repeat; background-position: right 14px center;
    background-color: rgba(245,200,0,0.04);
  }
  .f-select option { background: #1a1800; color: var(--white); }
  .f-textarea { resize: vertical; min-height: 110px; padding: 13px 16px; }

  /* Submit button */
  .submit-btn {
    display: inline-flex; align-items: center; gap: 10px;
    background: var(--accent); color: var(--black); border: none;
    padding: 15px 38px; font-size: 11px; letter-spacing: 2.5px; text-transform: uppercase;
    font-family: 'DM Sans', sans-serif; font-weight: 500; border-radius: 4px;
    cursor: pointer; transition: background .25s, transform .2s;
    align-self: flex-start; margin-top: 4px;
  }
  .submit-btn:hover    { background: var(--gold); transform: translateY(-2px); }
  .submit-btn:disabled { opacity: .65; cursor: not-allowed; transform: none; }
  .submit-btn svg      { width: 13px; height: 13px; }

  .form-disclaimer {
    font-size: 11px; color: var(--muted); line-height: 1.7; margin-top: 10px;
  }

  /* ── SUCCESS STATE ── */
  .success-state {
    display: flex; flex-direction: column; align-items: center;
    justify-content: center; text-align: center;
    padding: 60px 40px; gap: 16px;
    background: rgba(34,197,94,0.05);
    border: 1px solid rgba(34,197,94,0.2); border-radius: 8px;
    min-height: 420px;
  }
  .success-icon {
    width: 60px; height: 60px; border-radius: 50%;
    background: rgba(34,197,94,0.12);
    display: flex; align-items: center; justify-content: center;
    animation: scaleIn .4s ease;
  }
  @keyframes scaleIn {
    from { transform: scale(.6); opacity: 0; }
    to   { transform: scale(1);  opacity: 1; }
  }
  .success-icon svg {
    width: 28px; height: 28px; stroke: var(--success); fill: none;
    stroke-width: 2.5; stroke-linecap: round; stroke-linejoin: round;
  }
  .success-title { font-family: 'Bebas Neue', sans-serif; font-size: 28px; letter-spacing: 2px; color: var(--success); }
  .success-desc  { font-size: 14px; color: var(--muted-light); line-height: 1.7; max-width: 320px; }
  .success-reset {
    background: none; border: 1px solid rgba(34,197,94,0.25);
    color: var(--success); padding: 10px 24px; border-radius: 4px;
    cursor: pointer; font-size: 12px; letter-spacing: 1.5px; text-transform: uppercase;
    font-family: 'DM Sans', sans-serif; transition: background .2s; margin-top: 6px;
  }
  .success-reset:hover { background: rgba(34,197,94,0.08); }

  /* ── Responsive ── */
  @media (max-width: 960px) {
    .contact-inner  { grid-template-columns: 1fr; gap: 48px; }
    .contact-section{ padding: 72px 28px; }
  }
  @media (max-width: 640px) {
    .contact-section{ padding: 64px 16px; }
    .form-row-2     { grid-template-columns: 1fr; }
    .submit-btn     { width: 100%; justify-content: center; }
  }
`;

/* ─────────────────────────────────────────────
   CONTACT INFO DATA
───────────────────────────────────────────── */
const CONTACT_INFO = [
  {
    label: 'Email',
    text: 'hello@webdigiting.com',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="var(--accent)"
        strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
        <polyline points="22,6 12,13 2,6" />
      </svg>
    ),
  },
  {
    label: 'Phone',
    text: '+91 94757 08366',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="var(--accent)"
        strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.38 2 2 0 0 1 3.58 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.55a16 16 0 0 0 6.56 6.56l1.62-1.62a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
      </svg>
    ),
  },
  {
    label: 'Office',
    text: 'Ghatal, West Bengal, India — 721212',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="var(--accent)"
        strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
        <circle cx="12" cy="10" r="3" />
      </svg>
    ),
  },
];

/* ─────────────────────────────────────────────
   ICON INPUT WRAPPER
───────────────────────────────────────────── */
const IconInput = ({ icon, ...props }) => (
  <div className="input-wrap">
    <svg className="input-icon" viewBox="0 0 24 24" fill="none"
      strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      {icon}
    </svg>
    <input className="f-input has-icon" {...props} />
  </div>
);

/* ─────────────────────────────────────────────
   WHATSAPP ICON SVG
───────────────────────────────────────────── */
const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

/* ─────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────── */
const LandingContact = ({
  form, setForm,
  submitting, submitted,
  setSubmitted, handleSubmit,
}) => (
  <>
    <style>{styles}</style>

    <section className="contact-section" id="contact">
      <div className="contact-inner">

        {/* ── LEFT PANEL ── */}
        <div>
          <p className="contact-eyebrow">Get In Touch</p>
          <h2 className="contact-heading">
            Let's Build<br />Something <em>Great.</em>
          </h2>
          <p className="contact-desc">
            Ready to take your brand to the next level? Fill out the form and
            we'll get back to you within 24 hours.
          </p>

          {/* Contact info */}
          <div className="contact-info">
            {CONTACT_INFO.map((c) => (
              <div key={c.label} className="ci-item">
                <div className="ci-icon">{c.icon}</div>
                <div>
                  <p className="ci-label">{c.label}</p>
                  <p className="ci-text">{c.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── RIGHT PANEL ── */}
        {submitted ? (
          <div className="success-state">
            <div className="success-icon">
              <svg viewBox="0 0 24 24">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h3 className="success-title">Message Sent!</h3>
            <p className="success-desc">
              Thank you for reaching out. We'll get back to you within 24 hours
              with a personal response.
            </p>
            <button
              type="button"
              className="success-reset"
              onClick={() => setSubmitted(false)}
            >
              Send Another Message
            </button>
          </div>
        ) : (
          <div className="contact-form">

            {/* ── WhatsApp CTA ── */}
            <a
              className="wa-btn"
              href="https://wa.me/919475708366?text=Hi%2C%20I%27d%20like%20to%20discuss%20a%20project%20with%20you."
              target="_blank"
              rel="noopener noreferrer"
            >
              <div className="wa-icon-wrap">
                <WhatsAppIcon />
              </div>
              <div className="wa-text">
                <div className="wa-title">Start a WhatsApp Conversation</div>
                <div className="wa-sub">Get a faster response — we're usually online</div>
              </div>
              <div className="wa-arrow">→</div>
            </a>

            {/* ── Divider ── */}
            <div className="wa-divider">
              <span>Or fill the form</span>
            </div>

            {/* Row: Full Name + Email */}
            <div className="form-row-2">
              <div className="form-field">
                <label className="field-label">
                  Full Name <span className="field-required">*</span>
                </label>
                <IconInput
                  icon={<><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>}
                  type="text"
                  placeholder="Enter Your Full Name"
                  value={form.full_name}
                  onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
                />
              </div>
              <div className="form-field">
                <label className="field-label">
                  Email Address <span className="field-required">*</span>
                </label>
                <IconInput
                  icon={<><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></>}
                  type="email"
                  placeholder="Enter Your Email Address"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                />
              </div>
            </div>

            {/* Phone */}
            <div className="form-field">
              <label className="field-label">
                Phone Number <span className="field-required">*</span>
              </label>
              <IconInput
                icon={<path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.38 2 2 0 0 1 3.58 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.55a16 16 0 0 0 6.56 6.56l1.62-1.62a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />}
                type="tel"
                placeholder="Enter Your Phone Number"
                value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                required
              />
            </div>

            {/* Service select */}
            <div className="form-field">
              <label className="field-label">
                Service Interested In <span className="field-required">*</span>
              </label>
              <select
                className="f-select"
                value={form.service}
                onChange={(e) => setForm((f) => ({ ...f, service: e.target.value }))}
                required
              >
                <option value="">Select a service…</option>
                {SERVICES.map((s) => (
                  <option key={s.num || s.name || s} value={s.name || s}>
                    {s.name || s}
                  </option>
                ))}
                <option value="Multiple Services">Multiple Services</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* Message */}
            <div className="form-field">
              <label className="field-label">
                Tell Us About Your Project <span className="field-required">*</span>
              </label>
              <textarea
                className="f-textarea"
                placeholder="Describe your goals, challenges, and what success looks like for you…"
                value={form.message}
                onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
              />
            </div>

            {/* Submit */}
            <div>
              <button
                type="button"
                className="submit-btn"
                disabled={submitting}
                onClick={handleSubmit}
              >
                {submitting ? 'Sending…' : 'Send Message'}
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13" />
                  <polygon points="22 2 15 22 11 13 2 9 22 2" fill="currentColor" stroke="none" />
                </svg>
              </button>
              <p className="form-disclaimer">
                We respect your privacy. Your information will never be shared with third parties.
              </p>
            </div>

          </div>
        )}

      </div>
    </section>
  </>
);

export default LandingContact;